// ** React Imports
import React, { useState } from "react";

// ** Styles Imports
import Styles from "./styles.module.css";

// ** Component Imports
import Input from "../../../../../components/Input/Input";
import Button from "../../../../../components/Button/Button";
import PasswordRequirements from "../../../../user/components/signup/PasswordRequirements";

// ** Icons
import { FiEye, FiEyeOff, FiLock } from "react-icons/fi";

// ** API Imports
import { _changePassword } from "../../../redux/actions";

// ** Utils Imports
import {
  showErrorSnackbar,
  showSuccessSnackbar
} from "../../../../snackbar/helpers";
import { FormattedMessage } from "react-intl";

// ** Form Validation Imports
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

const schema = Yup.object().shape({
  oldPassword: Yup.string().required("Old password is required"),
  newPassword: Yup.string()
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/,
      "Password doesn't meet requirements"
    )
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
    .required("Password confirmation is required")
})

function ResetPassword() {
  // ** States
  const [loading, setLoading] = useState(false)
  const [oldPassword, setOldPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false
  })

  // ** Form Validation Hook
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onSubmit"
  })

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }))
  }

  // ** Handlers
  const onSubmit = () => {
    setLoading(true)
    _changePassword(
      {
        old_password: oldPassword,
        new_password: newPassword,
        confirm_password: confirmPassword
      },
      (data) => {
        setOldPassword("")
        setConfirmPassword("")
        setNewPassword("")
        setLoading(false)
        showSuccessSnackbar({ alertMessage: "Password successfully reset" })
      },
      (error) => {
        setLoading(false)
        showErrorSnackbar({
          alertMessage: error.errors.length ? (
            <FormattedMessage id={error.errors[0]} />
          ) : (
            "Unknown Error"
          )
        })
      }
    )
  }

  return (
    <div className={Styles.modernPasswordContainer}>
      <div className={Styles.passwordHeader}>
        <h2 className={Styles.passwordTitle}>Password</h2>
        <p className={Styles.passwordSubtitle}>
          Think of something unique and strong. Let's lock your account down tight!
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Current Password */}
        <div className={Styles.inputGroup}>
          <Input
            type={showPasswords.old ? "text" : "password"}
            variant="secondary"
            {...register("oldPassword")}
            inputLabel="Old Password"
            placeholder="••••••••••••"
            onChange={(e) => {
              register("oldPassword").onChange(e)
              setOldPassword(e.target.value)
            }}
            error={errors.oldPassword}
            containerStyle={{ position: "relative" }}
            rightIcon={
              <div className={Styles.passwordIcons}>
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('old')}
                  className={Styles.eyeButton}
                >
                  {showPasswords.old ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
                <div className={Styles.lockIcon}>
                  <FiLock size={12} />
                </div>
              </div>
            }
          />
        </div>

        {/* New Password Grid */}
        <div className={Styles.passwordGrid}>
          <div className={Styles.inputGroup}>
            <Input
              type={showPasswords.new ? "text" : "password"}
              variant="secondary"
              {...register("newPassword")}
              inputLabel="New Password"
              onChange={(e) => {
                register("newPassword").onChange(e)
                setNewPassword(e.target.value)
              }}
              containerStyle={{ width: "100%" }}
              error={errors.newPassword}
              rightIcon={
                <div className={Styles.passwordIcons}>
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    className={Styles.eyeButton}
                  >
                    {showPasswords.new ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                  </button>
                  <div className={Styles.lockIcon}>
                    <FiLock size={12} />
                  </div>
                </div>
              }
            />
          </div>

          <div className={Styles.inputGroup}>
            <Input
              type={showPasswords.confirm ? "text" : "password"}
              variant="secondary"
              {...register("confirmPassword")}
              inputLabel="Re-enter Password"
              onChange={(e) => {
                register("confirmPassword").onChange(e)
                setConfirmPassword(e.target.value)
              }}
              error={errors.confirmPassword}
              rightIcon={
                <div className={Styles.passwordIcons}>
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    className={Styles.eyeButton}
                  >
                    {showPasswords.confirm ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                  </button>
                  <div className={Styles.lockIcon}>
                    <FiLock size={12} />
                  </div>
                </div>
              }
            />
          </div>
        </div>

        {/* Password Requirements */}
        <div className={Styles.requirementsSection}>
          <PasswordRequirements
            password={newPassword}
            containerStyle={{ marginBottom: 0 }}
          />
        </div>

        {/* Help Text */}
        <div className={Styles.helpText}>
          <p>
            To ensure your account is well protected, please use 8 or more
            characters with a mix of letters, numbers & symbols.
          </p>
        </div>

        {/* Save Button */}
        <div className={Styles.buttonContainer}>
          <Button
            type="submit"
            disabled={confirmPassword !== newPassword || loading}
            isLoading={loading}
            text="Save Password"
            style={{
              width: "auto",
              minWidth: "140px",
              padding: "12px 32px"
            }}
          />
        </div>
      </form>
    </div>
  )
}

export default ResetPassword
